"use client";

import fetchGraphqlClient from "@workspace/nextjs_utils/src/fetchGraphqlClient";
import { useEffect, useState } from "react";
import { ITEMS_PER_PAGE } from "../constants";
import normalizeGamesResponse, {
  type SearchResultItem,
} from "../lib/normalizeGamesResponse";
import resolveMenuItem from "../lib/resolveMenuItem";
import { HOME_GAMES_QUERY } from "../queries/homeGames";
import { SEARCH_GAMES_QUERY } from "../queries/searchGames";
import { TOP_SEARCHED_GAMES } from "../queries/topSearchedGames";
import type { GameFragment } from "../types";

type CasinoGameInventoryResult = {
  casinoGameInventory?: {
    topSearchedGames?: GameFragment[] | null;
    games?: {
      edges?: ({ node?: GameFragment | null } | null)[] | null;
    } | null;
    searchGames?: {
      results?: (SearchResultItem | null)[] | null;
      totalCount?: number | null;
    } | null;
  } | null;
};

type useQueryProps = {
  queryType: "search" | "top_games";
  brand: string;
  countryCode?: string;
  searchTerm?: string;
  category?: string;
  selectedProviders?: string[];
  pagination?: {
    first: number;
    offset: number;
  };
  skip?: boolean;
};

function padToZero(n: number): number {
  if (n < 0) return 0;
  return n;
}

export function useQuery({
  queryType = "search",
  searchTerm,
  category,
  brand,
  countryCode,
  selectedProviders,
  pagination,
  skip = false,
}: useQueryProps): [GameFragment[], boolean, number] {
  const [data, setData] = useState<GameFragment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [nItems, setNItems] = useState(0);
  const [nItemsAdditionalLoading, setNItemsAdditionalLoading] =
    useState<number>(0);

  const hasProviderFilter = (selectedProviders?.length || 0) > 0;
  const useHomeGamesQuery =
    queryType === "top_games" && (selectedProviders?.length || 0) > 0;

  useEffect(() => {
    if (skip) return;

    let ignore = false;

    setNItemsAdditionalLoading(pagination?.offset || 0);
    const makeQuery = async (): Promise<void> => {
      try {
        const itemsToLoad = nItemsAdditionalLoading;
        setNItemsAdditionalLoading(0);

        // Skip the loading state if pagination is not the first page
        if (pagination?.offset === 0) {
          setLoading(true);
        }

        const firstVariable: number =
          itemsToLoad === 0 ? pagination?.first || ITEMS_PER_PAGE : itemsToLoad;
        const offsetVariable: number =
          itemsToLoad === 0
            ? pagination?.offset || 0
            : padToZero((pagination?.offset || 0) - itemsToLoad);

        const menuItem = hasProviderFilter
          ? undefined
          : resolveMenuItem(category, brand);

        const query = useHomeGamesQuery
          ? HOME_GAMES_QUERY
          : queryType === "top_games"
            ? TOP_SEARCHED_GAMES
            : SEARCH_GAMES_QUERY;

        const d = await fetchGraphqlClient<CasinoGameInventoryResult>(
          query,
          useHomeGamesQuery
            ? "games"
            : queryType === "top_games"
              ? "topSearchedGames"
              : "searchGames",
          useHomeGamesQuery
            ? {
                variables: {
                  filter: { site: brand, menuItem },
                  first: firstVariable,
                },
              }
            : queryType === "top_games"
              ? {
                  variables: { menuItem, site: brand, countryCode },
                }
              : {
                  variables: {
                    searchTerm,
                    site: brand,
                    countryCode,
                    menuItem,
                    first: firstVariable,
                    offset: offsetVariable,
                  },
                },
        );
        if (!ignore) {
          if (queryType === "top_games") {
            const items = useHomeGamesQuery
              ? (d?.data?.casinoGameInventory?.games?.edges || [])
                  .map((edge) => edge?.node)
                  .filter((node): node is GameFragment => Boolean(node))
              : d?.data?.casinoGameInventory?.topSearchedGames || [];
            setData(items);
            setNItems(items.length);
          } else {
            setData(
              normalizeGamesResponse(
                d?.data?.casinoGameInventory?.searchGames?.results,
              ),
            );
            setNItems(
              d?.data?.casinoGameInventory?.searchGames?.totalCount || 0,
            );
          }
          setLoading(false);
        }
      } catch {
        if (!ignore) {
          console.warn("[Search UI] Query failed...");
          setData([] as GameFragment[]);
          setLoading(false);
        }
      }
    };

    const delay = queryType === "top_games" ? 0 : 400;

    const timeoutId = setTimeout(() => makeQuery(), delay);
    return () => {
      ignore = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    queryType,
    searchTerm,
    category,
    hasProviderFilter,
    skip,
    brand,
    pagination?.offset,
    pagination?.first,
  ]);

  return [data, loading, nItems];
}
